/**
 * Determine whether a given router object has parameters or not.
 *
 * @param {VueRouter/Route} route The vue-router object
 *
 * @returns {boolean} true when the Vue-router has parameters
 */
function hasParams(route) {
    return !!Object.keys(route.params).length;
}

/**
 * Keep coordinates between navigation - used in router.beforeEach
 *
 * @param {VueRouter/Route} to vue-router route object to which we are navigating
 * @param {VueRouter/Route} from vue-router route object from which we are navigating
 * @param {Function} next vue-router resolve hook.
 *
 * @see VueRouter Navigation Guards <https://router.vuejs.org/guide/advanced/navigation-guards.html>
 */
export function keepCoordinates(to, from, next) {
    if (!hasParams(to) && hasParams(from)) {
        const toWithParams = { ...to, params: from.params };
        next(toWithParams);
    } else {
        next();
    }
}

/**
* functions for converting a mapview to a simple coordinate, zoom string and back. The String is similar to
* what is used in google maps
*/
export default {
    methods: {
        /**
        * Returns a well formatted coordinate string from either the center of the map or a provided LngLat object
        * @param {MapboxGLMap} map mapbox-gl map object
        * @param {LngLat} lngLat (Optional) mapbox-gl LngLat object, if not provided, the map center will be used
        */
        getMapCoordinates(map, lngLat = null) {
            // save LngLatBounds array as a JSON string
            let center = map.getCenter();
            if (lngLat !== null && lngLat.lng && lngLat.lat) {
                center = lngLat;
            }
            const zoom = map.getZoom();
            return `@${+(center.lat.toFixed(5))},${+(center.lng.toFixed(5))},${+(zoom.toFixed(3))}z`;
        },

        /**
         * Returns an object with a center and zoom value
         *
         * @param {String} coordString  Well Formed Coordinate string like @<lat>,<lng>,<zoom>z
         *
         * @return {Object} An object containing the center and zoom
         *
         * @throws {Exception} When the coordinates are malformed.
         */
        parseMapCoordinates(coordString) {
            // convert the object to an object
            if (!coordString.startsWith('@')) {
                throw new Error('Malformed map coordinates (missing expected @)');
            }
            if (!coordString.endsWith('z')) {
                throw new Error('Malformed map coordinates (missing expected z)');
            }
            const parts = coordString.substring(0, coordString.length - 1).substring(1).split(',');
            if (parts.length !== 3) {
                throw new Error(`Malformed map coordinates (expected three values, got ${parts.length})`);
            }
            return {
                center: [+parts[1], +parts[0]],
                zoom: +parts[2],
            };
        },

        /**
         * Sets the URL coordinates given the current map view.
         *
         * @param {MapboxGLMap} map - mapbox-gl map object
         * @param {string} coordinatesKey - URL parameter name for the location string
         */
        setMapCoordinates(map, coordinatesKey = 'coordinates') {
            const coords = this.getMapCoordinates(map);

            if (!(coordinatesKey in this.$route.params) || this.$route.params[coordinatesKey] !== coords) {
                const pathObject = {
                    params: {},

                    // preserve query string parameters
                    query: this.$route.query,
                };
                pathObject.params[coordinatesKey] = coords;
                this.$router.replace(pathObject);
            }
        },
    },
};
